import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  IconButton,
  Chip,
  Alert,
  Tooltip,
} from "@mui/material";
import { Add, Edit, Refresh, Visibility, Lock } from "@mui/icons-material";
import { format } from "date-fns";
import { Link, useNavigate } from "react-router-dom";
import OrganizationConfigService from "../../../services/OrganizationConfigService";
import AppService from "../../../services/AppService";
import { showNotification } from "../../../utils/notifications";

// Helper to decode JWT and extract organization id and roles
const getOrgInfoFromToken = () => {
  try {
    const authInfo = localStorage.getItem('authInfo');
    const token = authInfo ? JSON.parse(authInfo)?.token : null;
    if (!token) return null;
    const [, payloadB64] = token.split('.') || [];
    if (!payloadB64) return null;
    const payload = JSON.parse(atob(payloadB64));
    return {
      orgId: payload?.org || payload?.organizationId || null,
      roles: (payload?.roles || []).map(r => r.authority || r).concat(payload?.authorities || [])
    };
  } catch (e) {
    console.error('Failed to extract org from token', e);
    return null;
  }
};

/**
 * Organization Configuration List component
 * Displays a list of all organization configurations with options to create, edit, and view details
 */
const OrganizationConfigList = () => {
  // Initialize with empty array to prevent undefined errors
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [configDisabled, setConfigDisabled] = useState(null); // holds message when configuration is disabled
  const navigate = useNavigate();

  // Load organization configs on component mount
  useEffect(() => {
    fetchOrganizationConfigs();
  }, []);

  // If user is Customer Org Admin, redirect to edit page directly once we have data
  useEffect(() => {
    const info = getOrgInfoFromToken();
    const roles = info?.roles || [];
    const isCustomerAdmin = roles.includes('ROLE_CUSTOMER_ADMIN');
    if (!loading && isCustomerAdmin) {
      const first = Array.isArray(configs) && configs.length > 0 ? configs[0] : null;
      if (first?.id) {
        navigate(`/admin/organization-config/edit/${first.id}`, { replace: true, state: { config: first } });
      }
    }
  }, [loading, configs, navigate]);

  /**
   * Fetch all organization configurations from the API
   */
  const fetchOrganizationConfigs = () => {
    setLoading(true);
    setError(null);

    // First try admin list-all
    OrganizationConfigService.getAllOrganizationConfigs()
      .then((data) => {
        const arr = Array.isArray(data) ? data : [];
        // If API returns disabled object instead of an array, capture it
        if (!Array.isArray(data) && data && data.code === 'CONFIGURATION_DISABLED') {
          setConfigDisabled(data.message || 'Configuration disabled');
          setConfigs([]);
          setLoading(false);
          return;
        }
        // If ROOT_ADMIN, augment with all CUSTOMER orgs
        const info = getOrgInfoFromToken();
        const roles = info?.roles || [];
        const isRootAdmin = roles.includes('ROLE_ROOT_ADMIN');
        if (!isRootAdmin) {
          setConfigs(arr);
          setLoading(false);
          return;
        }
        // Fetch all CUSTOMER organizations and merge
        AppService.get('/v1/organisation?type=CUSTOMER')
          .then((orgs) => {
            const orgArray = Array.isArray(orgs) ? orgs : (Array.isArray(orgs?.data) ? orgs.data : []);
            const byOrgId = new Map(arr.map(c => [c.organizationId, c]));
            const merged = orgArray.map(org => {
              const cfg = byOrgId.get(org.id);
              if (cfg) {
                return { ...cfg, organizationName: cfg.organizationName || org.name };
              }
              // placeholder row for orgs without config
              return {
                id: null,
                organizationId: org.id,
                organizationName: org.name,
                allowSelfAssign: false,
                allow_workflow_self_assign: false,
                allowTicketSelfAssign: false,
                allowOrgUserAssignment: false,
                selfAssignLimit: '-',
                updatedAt: null,
              };
            });
            setConfigs(merged);
            setLoading(false);
          })
          .catch((e) => {
            console.error('Failed to load organizations for merge', e);
            setConfigs(arr);
            setLoading(false);
          });
      })
      .catch(async (error) => {
        // If API directly returned disabled in error payload, show lock state for customer admin
        const respData = error?.response?.data;
        const disabledCode = respData?.code;
        const disabledMsg = (typeof respData === 'string') ? respData : respData?.message;
        const msgLower = (disabledMsg || '').toLowerCase();
        if (
          disabledCode === 'CONFIGURATION_DISABLED' ||
          msgLower.includes('configuration_disabled') ||
          msgLower.includes('contact admin to enable configuration')
        ) {
          setConfigDisabled(disabledMsg || 'Configuration disabled');
          setConfigs([]);
          setLoading(false);
          return;
        }
        // If forbidden, attempt scoped fetch by org id
        if (error?.response?.status === 403) {
          const info = getOrgInfoFromToken();
          const orgId = info?.orgId;
          if (orgId) {
            try {
              const cfg = await OrganizationConfigService.getOrganizationConfigByOrganizationId(orgId);
              if (cfg && cfg.code === 'CONFIGURATION_DISABLED') {
                setConfigDisabled(cfg.message || 'Configuration disabled');
                setConfigs([]);
              } else {
                setConfigs(cfg ? [cfg] : []);
              }
              setLoading(false);
              return;
            } catch (e2) {
              // Check if scoped fetch also returned disabled
              const resp2 = e2?.response?.data;
              const code2 = resp2?.code;
              const msg2 = (typeof resp2 === 'string') ? resp2 : resp2?.message;
              const msg2Lower = (msg2 || '').toLowerCase();
              if (
                code2 === 'CONFIGURATION_DISABLED' ||
                msg2Lower.includes('configuration_disabled') ||
                msg2Lower.includes('contact admin to enable configuration')
              ) {
                setConfigDisabled(msg2 || 'Configuration disabled');
                setConfigs([]);
                setLoading(false);
                return;
              }
              console.error('Scoped fetch failed', e2);
              setError('Failed to load organization configuration');
              setConfigs([]);
              setLoading(false);
              return;
            }
          }
        }
        console.error("Error fetching organization configurations:", error);
        // As a UX fallback for customer admins, show disabled lock instead of generic error
        try {
          const info = getOrgInfoFromToken();
          const roles = info?.roles || [];
          const isCustomerAdmin = roles.includes('ROLE_CUSTOMER_ADMIN');
          if (isCustomerAdmin) {
            setConfigDisabled('Contact admin to enable configuration for organization');
            setConfigs([]);
            setLoading(false);
            return;
          }
        } catch (_) { /* ignore token parsing issues */ }
        setError("Failed to load organization configurations");
        setConfigs([]);
        setLoading(false);
      });
  };

  /**
   * Format date string for display
   * @param {string} dateStr Date string to format
   * @returns {string} Formatted date string or 'Not available'
   */
  const formatDate = (val) => {
    if (val === null || val === undefined || val === '-') return "Not available";
    try {
      let ts = val;
      if (typeof ts === 'string') {
        const parsed = Number(ts);
        ts = isNaN(parsed) ? ts : parsed;
      }
      // If ts is a number representing epoch seconds, convert to ms
      if (typeof ts === 'number') {
        // Heuristic: seconds are typically < 1e12, ms are >= 1e12 in modern epoch
        if (ts < 1e12) ts = ts * 1000;
        return format(new Date(ts), "yyyy-MM-dd HH:mm:ss");
      }
      // Fallback: let Date parse strings like ISO
      return format(new Date(ts), "yyyy-MM-dd HH:mm:ss");
    } catch (error) {
      return "Not available";
    }
  };

  return (
    <Card variant="outlined" sx={{ mb: 3 }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h5" component="h2">
            Organization Configurations
          </Typography>
          <Box>
            <Button
              variant="outlined"
              color="primary"
              size="small"
              startIcon={<Refresh />}
              onClick={fetchOrganizationConfigs}
              sx={{ mr: 1 }}
            >
              Refresh
            </Button>
            {(() => {
              const info = getOrgInfoFromToken();
              const roles = info?.roles || [];
              const isAdmin = roles.includes('ROLE_ROOT_ADMIN') || roles.includes('ROLE_ADMIN');
              const isCustomerAdmin = roles.includes('ROLE_CUSTOMER_ADMIN');
              // Hide Create button when customer admin config is disabled, regardless of admin flags
              if (isCustomerAdmin && configDisabled) return null;
              if (!isAdmin) return null;
              return (
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  startIcon={<Add />}
                  component={Link}
                  to="/admin/organization-config/create"
                >
                  Create New
                </Button>
              );
            })()}
          </Box>
        </Box>

        {(() => {
          const info = getOrgInfoFromToken();
          const roles = info?.roles || [];
          const isCustomerAdmin = roles.includes('ROLE_CUSTOMER_ADMIN');
          // If showing disabled lock for customer admin, suppress error banner
          if (isCustomerAdmin && configDisabled) return null;
          return error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : null;
        })()}

        {loading ? (
          <Box display="flex" justifyContent="center" my={4}>
            <CircularProgress />
          </Box>
        ) : (() => {
          const info = getOrgInfoFromToken();
          const roles = info?.roles || [];
          const isCustomerAdmin = roles.includes('ROLE_CUSTOMER_ADMIN');
          // If there's an error, don't render the empty-state or table again
          if (error) {
            return null;
          }
          if (isCustomerAdmin && configDisabled) {
            return (
              <Box display="flex" alignItems="center" justifyContent="center" flexDirection="column" sx={{ my: 6 }}>
                <Lock color="disabled" sx={{ fontSize: 64, mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Configuration Disabled
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {configDisabled || 'Contact admin to enable configuration for organization'}
                </Typography>
              </Box>
            );
          }
          if (!configs || configs.length === 0) {
            return (
              <Alert severity="info">
                No organization configurations found. Click 'Create New' to add a configuration.
              </Alert>
            );
          }
          return (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Organization</TableCell>
                    <TableCell align="center">Self-Assign</TableCell>
                    <TableCell align="center">Ticket Self-Assign</TableCell>
                    <TableCell align="center">Org User Assignment</TableCell>
                    <TableCell align="center">Assign Limit</TableCell>
                    <TableCell align="center">Last Updated</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {configs.map((config) => (
                    <TableRow key={config.id}>
                      <TableCell>{config.organizationName}</TableCell>
                      <TableCell align="center">
                        {(() => {
                          const allowSelf = (config.allowSelfAssign === true) || (config.allow_workflow_self_assign === true);
                          return (
                            <Chip
                              label={allowSelf ? "Enabled" : "Disabled"}
                              color={allowSelf ? "success" : "default"}
                              size="small"
                            />
                          );
                        })()}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={config.allowTicketSelfAssign ? "Enabled" : "Disabled"}
                          color={config.allowTicketSelfAssign ? "success" : "default"}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={config.allowOrgUserAssignment ? "Enabled" : "Disabled"}
                          color={config.allowOrgUserAssignment ? "success" : "default"}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">{config.selfAssignLimit}</TableCell>
                      <TableCell align="center">{formatDate(config.updatedAt)}</TableCell>
                      <TableCell align="center">
                        {(() => {
                          const info = getOrgInfoFromToken();
                          const roles = info?.roles || [];
                          const isCustomerAdmin = roles.includes('ROLE_CUSTOMER_ADMIN');
                          const isRootAdmin = roles.includes('ROLE_ROOT_ADMIN');
                          const hasConfig = !!config.id;
                          const orgId = config.organizationId;
                          return (
                            <>
                              {!isCustomerAdmin && hasConfig && (
                                <Tooltip title="View Details">
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    component={Link}
                                    to={`/admin/organization-config/view/${config.id}`}
                                  >
                                    <Visibility fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {hasConfig ? (
                                <Tooltip title="Edit Configuration">
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    component={Link}
                                    to={`/admin/organization-config/edit/${config.id}`}
                                  >
                                    <Edit fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              ) : (
                                isRootAdmin && (
                                  <Tooltip title="Create Configuration">
                                    <Button
                                      variant="outlined"
                                      size="small"
                                      component={Link}
                                      to={`/admin/organization-config/create?organizationId=${orgId}`}
                                    >
                                      Create
                                    </Button>
                                  </Tooltip>
                                )
                              )}
                            </>
                          );
                        })()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          );
        })()}
      </CardContent>
    </Card>
  );
};

export default OrganizationConfigList;
